import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { JOBS_PAGE_SIZE } from "../configs/constants";
import { JOBS_QUERY_KEYS } from "../configs/queryKeys";
import { getJobs, getJob } from "../services/jobs.service";
import type { GetJobsServiceRequestParams } from "../types/jobs.types";

// Re-export for backward compatibility
export { JOBS_PAGE_SIZE } from "../configs/constants";
export { JOBS_QUERY_KEYS } from "../configs/queryKeys";

/**
 * Hook to fetch jobs with infinite pagination
 * @param params - Query parameters for filtering and pagination
 * @returns TanStack Query infinite query result
 */
export const useInfiniteJobs = (params?: Omit<GetJobsServiceRequestParams, "page">) => {
  return useInfiniteQuery({
    queryKey: [...JOBS_QUERY_KEYS.JOBS_INFINITE, params],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await getJobs({
        ...params,
        page: pageParam,
        limit: JOBS_PAGE_SIZE,
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      const { current_page, total_pages } = lastPage.metadata || {};
      return current_page && total_pages && current_page < total_pages
        ? current_page + 1
        : undefined;
    },
    initialPageParam: 1,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
};

/**
 * Hook to fetch a single job by ID
 * @param jobId - Job ID to fetch
 * @returns TanStack Query result with job data
 */
export const useJobQuery = (jobId: string) => {
  return useQuery({
    queryKey: JOBS_QUERY_KEYS.JOB_SINGLE(jobId),
    queryFn: async () => {
      const response = await getJob(jobId);
      return response.data;
    },
    enabled: !!jobId,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
};

/**
 * Hook to fetch jobs for a single page (regular query)
 * @param params - Query parameters for filtering and pagination
 * @returns TanStack Query result with jobs data
 */
export const useJobsQuery = (params?: GetJobsServiceRequestParams) => {
  return useQuery({
    queryKey: [...JOBS_QUERY_KEYS.JOBS, params],
    queryFn: async () => {
      const response = await getJobs(params || {});
      return response.data;
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
};
